// Typed view over the dog lane's reviewable cast document (dog-cast.v1).
//
// The orchestrator writes this beside the Echo's script purely so a human can
// look at it. NOTHING in the pipeline reads it, which is the point: production
// runs the grouping in `observe` mode permanently, we can see exactly what the
// GPT call found, and the delivered Echo is byte-identical to one where the
// lane never ran.
//
// Loading lives in `useDogCast`; this file is pure so the parsing and the
// pointer selection are testable without a browser.

import type { EchoflicksSessionEvent } from "../types";

export const DOG_CAST_SCHEMA_VERSION = "dog-cast.v1";

export type DogCastMember = {
  castId: string;
  breed: string;
  breedConfidence: string;
  descriptor: string;
  displayName: string | null;
  nameSource: string;
  representativeFilename: string;
  memberFilenames: string[];
  photoCount: number | null;
};

export type DogCast = {
  workloadId: string;
  modelId: string;
  mode: string;
  dogCount: number;
  countConfidence: string;
  regroupCount: number;
  dogs: DogCastMember[];
};

/** What the dashboard event tells us before the document is fetched. */
export type DogCastPointer = {
  castKey: string;
  castDogCount: number;
  occurredAt: string | null;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asNullableNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

/**
 * The latest `media.dog_census.observed` event that carries a cast document.
 *
 * Latest, not first: the census re-runs when photos arrive after `/create`,
 * and an earlier run describes a media set the user has since added to. But
 * only events WITH a `cast_s3_key` count — a later census that found no dogs
 * (or predates cast documents) must not hide an earlier reviewable cast.
 */
export function dogCastPointerFromEvents(
  events: EchoflicksSessionEvent[],
): DogCastPointer | null {
  const withCast = (events ?? [])
    .filter((event) => event.event_type === "media.dog_census.observed")
    .filter((event) => {
      const payload = asRecord(event.raw_payload);
      if (!payload) return false;
      const key = payload.cast_s3_key;
      return typeof key === "string" && key.length > 0;
    })
    .sort((left, right) =>
      String(left.occurred_at ?? "").localeCompare(
        String(right.occurred_at ?? ""),
      ),
    );
  const latest = withCast[withCast.length - 1];
  if (!latest) return null;
  const payload = asRecord(latest.raw_payload) ?? {};
  return {
    castKey: asString(payload.cast_s3_key),
    castDogCount: asNumber(payload.cast_dog_count),
    occurredAt: latest.occurred_at ?? null,
  };
}

export function parseDogCast(raw: unknown): DogCast | null {
  const document = asRecord(raw);
  if (!document) return null;
  if (asString(document.schema_version) !== DOG_CAST_SCHEMA_VERSION)
    return null;
  const policy = asRecord(document.policy) ?? {};
  const dogs = Array.isArray(document.dogs) ? document.dogs : [];
  return {
    workloadId: asString(document.workload_id),
    modelId: asString(policy.model_id),
    mode: asString(policy.mode, "observe"),
    dogCount: asNumber(document.dog_count),
    countConfidence: asString(document.count_confidence, "unknown"),
    regroupCount: asNumber(document.regroup_count),
    dogs: dogs.map(parseDog).filter((dog): dog is DogCastMember => !!dog),
  };
}

function parseDog(raw: unknown): DogCastMember | null {
  const dog = asRecord(raw);
  if (!dog) return null;
  return {
    castId: asString(dog.cast_id),
    breed: asString(dog.breed),
    breedConfidence: asString(dog.breed_confidence, "unknown"),
    descriptor: asString(dog.descriptor),
    displayName: asString(dog.display_name) || null,
    nameSource: asString(dog.name_source, "none"),
    representativeFilename: asString(dog.representative_filename),
    memberFilenames: stringList(dog.member_filenames),
    photoCount: asNullableNumber(dog.photo_count),
  };
}

function stringList(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((value): value is string => typeof value === "string")
    : [];
}

/**
 * Card title for one dog. Names only appear when the user gave them — an
 * unnamed group is "Dog 2", never a guess. The lane must not put a name on a
 * dog the user never named, and neither must this panel.
 */
export function dogTitleLabel(dog: DogCastMember, index: number): string {
  return dog.displayName ?? `Dog ${index + 1}`;
}

/** Badge text explaining where the name came from (or that there is none). */
export function dogNameSourceLabel(dog: DogCastMember): string {
  return dog.displayName ? dog.nameSource.replace("_", " ") : "unnamed";
}

export function dogPhotoCountLabel(count: number): string {
  return `${count} photo${count === 1 ? "" : "s"}`;
}
